import React, { forwardRef, useState } from 'react';
import PropTypes, {
  func, string,
} from 'prop-types';
import {
  EVBox,
  EVTypography,
  EVTagIcon,
  EVNoteIcon,
  EVPaperClipIcon,
  EVCheckbox,
  EVButton,
  useTheme,
} from '@eagleview/ev-comp-library';
import { useSelector, useDispatch } from 'react-redux';
import { ASSESS_IMAGES_API_ENDPOINT } from 'constants.js';
import * as sharedUtil from 'utils/utils';
import clsx from 'clsx';
import { useParams } from 'react-router-dom';
import { ASSESS_ROOF_DECISION_UNDERWRITING, FEATURE_ENTITLEMENTS } from 'layout/entitleUser/EntitleUser.constants';
import { get } from 'lodash';
import * as authUtils from 'utils/auth.utils';
import { orderStatuses } from 'layout/adjuster/Adjuster.constants';
import * as action from '../../Adjuster.actions';
import UnableToCaptureComponent from './UnableToCaptureComponent';
import useStyles from '../AdjusterSidePanel.styles';

const GalleryImageComponent = forwardRef(({
  categoryTag, selectedImage, setSelectedImage, filteredGalleryImagesWithIcons,
}, ref) => {
  // Selectors
  const authToken = useSelector((state) => state.adjusterReducer.accessToken);
  const isRoofDecisionUnderwritingEnabled = useSelector((state) => get(
    state.entitleUserReducer.featureFlags,
    ASSESS_ROOF_DECISION_UNDERWRITING,
    false,
  ));
  // Local state for selected images (only when roof decision underwriting is enabled)
  const [selectedImageUrns, setSelectedImageUrns] = useState([]);

  // utility hooks
  const styles = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { id: orderId } = useParams();

  // selectors
  const entitlements = useSelector((state) => state.entitleUserReducer.entitlements);
  const status = useSelector((state) => state.adjusterReducer.orderStatus);
  const isOrderBeingReset = useSelector((state) => state.adjusterReducer.isOrderBeingReset);
  const editAllowed = ((status === orderStatuses.READYFORREVIEW)
  && (isOrderBeingReset === false)
   && (authUtils.isEntitled(entitlements, FEATURE_ENTITLEMENTS.MANAGE_DECISIONS)));

  // Get images for this category
  const categoryImages = filteredGalleryImagesWithIcons.filter(
    (image) => image.tags.includes(categoryTag) && (image.utcTags === undefined || image.utcTags.length === 0),
  );
  const utcImages = filteredGalleryImagesWithIcons.filter(
    (image) => image.tags.includes(categoryTag) && image.utcTags !== undefined && Array.isArray(image.utcTags) && image.utcTags.length > 0,
  );

  // Feature flag enabled: Selection functionality
  // Check if all images in this category are selected
  const allImagesInCategory = [...categoryImages, ...utcImages];
  const allCategoryUrns = allImagesInCategory.map((img) => img.urn);
  const allSelected = isRoofDecisionUnderwritingEnabled && allCategoryUrns.length > 0 && allCategoryUrns.every((urn) => selectedImageUrns.includes(urn));
  const someSelected = isRoofDecisionUnderwritingEnabled && !allSelected && allCategoryUrns.some((urn) => selectedImageUrns.includes(urn));

  // Check if any images in this category are selected
  const hasSelectedImagesInCategory = isRoofDecisionUnderwritingEnabled && allCategoryUrns.some((urn) => selectedImageUrns.includes(urn));

  // Handle Select All checkbox change
  const handleSelectAllChange = (event) => {
    if (!isRoofDecisionUnderwritingEnabled) return;
    const isChecked = event.target.checked;
    if (isChecked) {
      // Add all category images to selection without duplicates
      const newSelectedUrns = [...new Set([...selectedImageUrns, ...allCategoryUrns])];
      setSelectedImageUrns(newSelectedUrns);
    } else {
      // Remove all category images from selection
      const newSelectedUrns = selectedImageUrns.filter((urn) => !allCategoryUrns.includes(urn));
      setSelectedImageUrns(newSelectedUrns);
    }
  };

  // Handle individual image checkbox change
  const handleImageCheckboxChange = (event, urn) => {
    if (!isRoofDecisionUnderwritingEnabled) return;

    event.stopPropagation();
    if (selectedImageUrns.includes(urn)) {
      // Remove from selection
      setSelectedImageUrns(selectedImageUrns.filter((selectedUrn) => selectedUrn !== urn));
    } else {
      // Add to selection
      setSelectedImageUrns([...selectedImageUrns, urn]);
    }
  };

  // Handle Include button click
  const handleIncludeClick = () => {
    if (!isRoofDecisionUnderwritingEnabled) return;

    // Get only the selected images from this category
    const selectedImagesInCategory = selectedImageUrns.filter((urn) => allCategoryUrns.includes(urn));

    // Include selected images from this category in the report by dispatching the action
    dispatch(action.saveImageReportInclusionAction({
      orderId,
      data: { referenceIds: selectedImagesInCategory },
    }));

    // Uncheck all selected images from this category
    const remainingSelectedUrns = selectedImageUrns.filter((urn) => !allCategoryUrns.includes(urn));
    setSelectedImageUrns(remainingSelectedUrns);
  };

  return (
    <EVBox ref={ref} className={styles.categoryComponentBox}>
      {isRoofDecisionUnderwritingEnabled ? (
        // Roof decision underwriting enabled: render with checkboxes and include button
        <EVBox className={styles.categoryTitleContainer}>
          <EVTypography className={styles.categoryComponentTitle}>{categoryTag}</EVTypography>
          <EVBox display="flex" alignItems="center" flexShrink={0}>
            <EVCheckbox
              checked={allSelected}
              indeterminate={someSelected}
              onChange={handleSelectAllChange}
              className={styles.selectAllCheckbox}
              style={{
                color: someSelected || allSelected ? theme.evPalette.ocean[700] : undefined,
                padding: theme.spacing(0.5),
              }}
              disabled={!editAllowed}
              size="small"
            />
            <EVTypography className={editAllowed ? styles.selectAllText : styles.selectAllTextDisabled}>Select All</EVTypography>
            <EVButton
              className={styles.includeButton}
              disabled={editAllowed ? !hasSelectedImagesInCategory : true}
              onClick={handleIncludeClick}
            >
              <EVPaperClipIcon
                className={styles.includeIcon}
                style={{
                  color: !hasSelectedImagesInCategory ? theme.evPalette.charcoal[600] : 'inherit',
                }}
              />
              <EVTypography className={styles.includeText}>Include</EVTypography>
            </EVButton>
          </EVBox>
        </EVBox>
      ) : (
        // Feature disabled: original implementation without checkboxes
        <EVTypography className={styles.categoryComponentTitle}>{categoryTag}</EVTypography>
      )}
      <EVBox className={styles.galleryTab}>
        {categoryImages.map((image) => {
          const imageIsSelected = selectedImage === image.urn;
          const hasEitherTagsNotesIncluded = image.tagsIncluded || image.notesIncluded || image.reportIncluded;
          const imageOrientation = image.orientation ? image.orientation : 360;
          const isChecked = isRoofDecisionUnderwritingEnabled && selectedImageUrns.includes(image.urn);

          return (
            <EVBox
              onClick={() => setSelectedImage(image.urn)}
              className={styles.thumbnailWrapper}
              key={image.urn}
              position="relative"
            >
              {isRoofDecisionUnderwritingEnabled && (
                <EVBox
                  className={styles.imageCheckboxContainer}
                  onClick={(e) => e.stopPropagation()}
                >
                  <EVCheckbox
                    checked={isChecked}
                    onChange={(e) => handleImageCheckboxChange(e, image.urn)}
                    className={styles.whiteCheckbox}
                    disabled={!editAllowed}
                    size="small"
                  />
                </EVBox>
              )}
              <img
                style={{ transform: `rotate(${360 - imageOrientation}deg)` }}
                className={clsx(styles.thumbnailStyle, imageIsSelected && styles.thumbnailSelected)}
                src={`${ASSESS_IMAGES_API_ENDPOINT}/${image.urn}/thumb?width=100&height=100&${sharedUtil.getAuthParam(authToken)}`}
                alt={image.urn}
              />
              {hasEitherTagsNotesIncluded ? (
                <EVBox className={clsx(styles.thumbnailOverlay, imageIsSelected && styles.thumbnailOverlaySelected)}>
                  <EVTagIcon className={clsx(styles.tagIconStyle, !image.tagsIncluded && styles.iconOpacity)} />
                  <EVNoteIcon className={clsx(styles.tagIconStyle, !image.notesIncluded && styles.iconOpacity)} />
                  <EVPaperClipIcon className={clsx(styles.tagIconStyle, !image.reportIncluded && styles.iconOpacity)} />
                </EVBox>
              ) : null}
            </EVBox>
          );
        })}
      </EVBox>
      {utcImages.map((image) => {
        const imageIsSelected = selectedImage === image.urn;
        const imageOrientation = image.orientation ? image.orientation : 360;
        const hasEitherTagsNotesIncluded = image.tagsIncluded || image.notesIncluded || image.reportIncluded;
        const isChecked = isRoofDecisionUnderwritingEnabled && selectedImageUrns.includes(image.urn);

        return (
          <EVBox key={image.urn} position="relative">
            <UnableToCaptureComponent
              image={image}
              setSelectedImage={setSelectedImage}
              imageIsSelected={imageIsSelected}
              imageOrientation={imageOrientation}
              hasEitherTagsNotesIncluded={hasEitherTagsNotesIncluded}
              isChecked={isChecked}
              onCheckboxChange={(e) => handleImageCheckboxChange(e, image.urn)}
              isRoofDecisionUnderwritingEnabled={isRoofDecisionUnderwritingEnabled}
            />
          </EVBox>
        );
      })}
    </EVBox>
  );
});

GalleryImageComponent.propTypes = {
  categoryTag: string.isRequired,
  selectedImage: string.isRequired,
  filteredGalleryImagesWithIcons: PropTypes.instanceOf(Array).isRequired,
  setSelectedImage: func.isRequired,
};

export default GalleryImageComponent;
